import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

// Import animations directly as modules
import checkAnimation from '@/assets/lottie/check.json';
import confettiAnimation from '@/assets/lottie/confetti.json';
import { ButtonLoadable } from '@/components/ui/button-loadable';
import { useUser } from '@/hooks/auth';
import { OnboardingRoutes } from '@/router/routes';
import { useRootSelector } from '@/router/state/root/util/context/helpers';
import { logger } from '@/utils/logger/logger';

import { completeOnboarding } from '../api/completion.api';
import { LottieAnimation } from '../components/lottie-animation';

const TAG = 'OnboardingCompletionPage:';

/**
 * Onboarding completion screen displaying celebration animations and workspace transition.
 *
 * Features:
 * - Confetti and check mark Lottie animations
 * - Welcome message and description text
 * - "Go to Workspace" button with loading state
 * - Error handling with toast notifications
 * - Automatic transition to main application flow
 */
export function OnboardingCompletionPage() {
  const { t } = useTranslation('common');
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useUser();
  const state = useRootSelector(state => state);
  const { onboardingMachine } = state.children;
  const navigationHandlersRef = useRef<{
    popState?: (event: PopStateEvent) => void;
  }>({});

  // Prevent back navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      event.preventDefault();
      // Push state again to prevent going back
      window.history.pushState(null, '', OnboardingRoutes.OnboardingCompletion);
    };

    // Store handler for cleanup
    navigationHandlersRef.current.popState = handlePopState;

    // Add extra history entries to make back button harder to use
    window.history.pushState(null, '', OnboardingRoutes.OnboardingCompletion);

    // Add event listener
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Redirect to login if not authenticated
  if (!user) {
    return null;
  }

  const handleGoToWorkspace = async () => {
    setIsLoading(true);

    try {
      logger.debug(TAG, 'Completing onboarding...');

      // Complete the onboarding process via API
      await completeOnboarding();

      logger.debug(TAG, 'Onboarding completed successfully');

      // Clean up navigation prevention before leaving
      if (navigationHandlersRef.current.popState) {
        window.removeEventListener('popstate', navigationHandlersRef.current.popState);
      }

      // Send COMPLETE event to onboarding machine to trigger transition to main state
      onboardingMachine?.send({ type: 'COMPLETE' });
    } catch (error) {
      logger.error(TAG, 'Failed to complete onboarding:', error);

      // Still transition even on error, as the user should be able to access the app
      onboardingMachine?.send({ type: 'COMPLETE' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background">
      {/* Confetti Animation - Full Screen Background Layer */}
      <LottieAnimation
        animationData={confettiAnimation}
        className="pointer-events-none absolute inset-0 z-0 h-full w-full"
        loop={true}
        autoplay={true}
      />

      {/* Content Container */}
      <div className="relative z-10 mx-auto flex max-w-md flex-col items-center justify-center px-6 text-center">
        {/* Check Mark Animation - Foreground Layer */}
        <div className="mb-8">
          <LottieAnimation
            animationData={checkAnimation}
            className="mx-auto h-20 w-20"
            loop={false}
            autoplay={true}
          />
        </div>

        {/* Content */}
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-foreground">{t('onboarding.completion.title')}</h1>

          <p className="max-w-sm text-sm leading-relaxed text-muted-foreground">
            {t('onboarding.completion.description')}
          </p>

          <ButtonLoadable
            onClick={handleGoToWorkspace}
            isLoading={isLoading}
            disabled={isLoading}
            className="mt-8 px-8 py-3 font-medium"
            size="lg"
          >
            {t('onboarding.completion.goToWorkspace')}
          </ButtonLoadable>
        </div>
      </div>
    </div>
  );
}
